import axios from 'axios'
import { parseAboutCollageFromCms } from './aboutCollage'
import { cmsClient, CMS_BASE_URL } from './client'
import {
  cmsDebug,
  extractMediaUrl,
  getStrapiFields,
  isCmsDebugEnabled,
  pickStrapiComponentList,
  pickCmsText,
  pickCmsMultilineText,
  sortByOrder,
  resolveCmsEntryOrder,
  toSlug,
} from './helpers'
import type {
  AboutFooterStat,
  ContactContent,
  ContactSubmissionPayload,
  FaqItem,
  HomeCmsContent,
  MetadataContent,
  OrganizationContent,
  ServiceItem,
  ServicePageContent,
  ServicePageFeature,
  ServicePageTech,
} from './types'
import { parseCmsSeo } from '@/seo/parseCmsSeo'

/** Reuse CMS responses within a session (avoids duplicate loads from Strict Mode / navigation). */
const CMS_RESPONSE_CACHE_MS = 5 * 60 * 1000
const cmsResponseCache = new Map<string, { at: number; value: unknown }>()
const cmsInflight = new Map<string, Promise<unknown>>()

let cachedServicePagesList: ServicePageContent[] | null = null
let cachedServicePagesListAt = 0

const EMPTY_HOME_CMS: HomeCmsContent = {
  hero: { title: '', primaryCta: '', backgroundImage: '' },
  business: { title: '', description: '', items: [] },
  services: { title: '', description: '', cta: '', items: [] },
  clearPath: { title: '', description: '', steps: [] },
  partners: [],
  about: {
    title: '',
    description: '',
    cta: '',
    image: '',
    images: [],
    collageBySlot: {},
    footerStats: [],
  },
  contact: { title: '', description: '', email: '', phone: '', address: '', cta: '' },
  metadata: { seoTitle: '', seoDescription: '', favicon: '', seo: null },
  organization: null,
  faqs: [],
}

const str = (val: unknown): string => (val == null ? '' : String(val))

/** First non-empty string from Strapi field name variants */
const pickStr = (...values: unknown[]): string => {
  for (const v of values) {
    const s = str(v).trim()
    if (s) return s
  }
  return ''
}

const parseAboutFooterStat = (field: unknown): AboutFooterStat => {
  if (!field) return { title: '', description: '' }
  if (Array.isArray(field) && field.length) return parseAboutFooterStat(field[0])
  if (typeof field === 'object') {
    const o = field as Record<string, unknown>
    if (o.data != null && !Array.isArray(o.data)) return parseAboutFooterStat(o.data)
  }
  const f = getStrapiFields(field)
  return {
    title: str(f.Title ?? f.title),
    description: str(f.Description ?? f.description),
  }
}

const parseAboutFooterStats = (aboutFields: Record<string, unknown>): AboutFooterStat[] => [
  parseAboutFooterStat(aboutFields.About_footer_1 ?? aboutFields.about_footer_1),
  parseAboutFooterStat(aboutFields.About_footer_2 ?? aboutFields.about_footer_2),
  parseAboutFooterStat(aboutFields.About_footer_3 ?? aboutFields.about_footer_3),
]

/** Icon / logo field may be `Icon`, `icon`, or nested media */
function pickMediaField(record: Record<string, unknown>): unknown {
  return record.Icon ?? record.icon ?? record.Logo ?? record.logo
}

function parseHeroFromPrimaryCta(raw: unknown): HomeCmsContent['hero'] {
  if (!raw || typeof raw !== 'object') {
    return { title: '', primaryCta: '', backgroundImage: '' }
  }

  const envelope = raw as { data?: unknown }
  const root = envelope.data
  if (!root) {
    return { title: '', primaryCta: '', backgroundImage: '' }
  }

  const fields = getStrapiFields(root)
  const title =
    pickCmsMultilineText(
      fields.Title,
      fields.title,
      fields.Headline,
      fields.headline,
      fields.Header,
      fields.header
    ) || pickStr(fields.Title, fields.title, fields.Headline, fields.headline)

  return {
    title,
    primaryCta: pickStr(
      fields.Primary_CTA,
      fields.primary_cta,
      fields.PrimaryCta,
      fields.CTA,
      fields.cta
    ),
    backgroundImage: extractMediaUrl(
      fields.backgroundImage ?? fields.BackgroundImage ?? fields.background_image
    ),
  }
}

const PRIMARY_CTA_PATH = '/api/primary-cta'

/** Hero single type — tracked separately so 403/400 surfaces in UI. */
async function fetchPrimaryCtaHeroRaw(): Promise<{ raw: unknown; httpStatus?: number }> {
  const path = PRIMARY_CTA_PATH
  const requestPath =
    isCmsDebugEnabled() ? `${path}${path.includes('?') ? '&' : '?'}_=${Date.now()}` : path

  const cached = cmsResponseCache.get(path)
  if (cached && Date.now() - cached.at < CMS_RESPONSE_CACHE_MS) {
    cmsDebug('primary-cta cache hit')
    return { raw: cached.value, httpStatus: 200 }
  }

  try {
    const res = await cmsClient.get(requestPath)
    const body = res.data
    if (body && typeof body === 'object' && 'data' in body && (body as { data: unknown }).data === null) {
      cmsDebug('primary-cta empty (data: null)')
      return { raw: body, httpStatus: 200 }
    }
    cmsDebug('primary-cta OK')
    cmsResponseCache.set(path, { at: Date.now(), value: body })
    return { raw: body, httpStatus: 200 }
  } catch (err) {
    const httpStatus = axios.isAxiosError(err) ? err.response?.status : undefined
    if (httpStatus === 403) {
      cmsDebug(
        'primary-cta FORBIDDEN (403) — enable Public find on Primary CTA in Strapi, or set VITE_CMS_API_TOKEN'
      )
    } else {
      cmsDebug('primary-cta FAILED', httpStatus ?? 'network', err instanceof Error ? err.message : err)
    }
    return { raw: null, httpStatus }
  }
}

function parseContactFields(contactRaw: unknown): ContactContent {
  const contactRoot =
    contactRaw && typeof contactRaw === 'object'
      ? (contactRaw as { data?: unknown }).data
      : null
  const contactFields = contactRoot ? getStrapiFields(contactRoot) : {}
  return {
    title: pickCmsText(contactFields.Title, contactFields.title),
    description: pickCmsText(contactFields.Description, contactFields.description),
    email: pickCmsText(contactFields.Email, contactFields.email),
    phone: pickCmsText(contactFields.Phone, contactFields.phone),
    address: pickCmsText(contactFields.Address, contactFields.address),
    cta: pickCmsText(contactFields.CTA, contactFields.cta),
  }
}

function parseMetadataFields(metadataRaw: unknown): MetadataContent {
  const metadataRoot =
    metadataRaw && typeof metadataRaw === 'object'
      ? (metadataRaw as { data?: unknown }).data
      : null
  const metadataFields = getStrapiFields(metadataRoot)
  const legacySeo = parseCmsSeo({
    metaTitle: metadataFields.SeoTitle,
    metaDescription: metadataFields.SeoDescription,
  })

  return {
    seoTitle: str(metadataFields.SeoTitle),
    seoDescription: str(metadataFields.SeoDescription),
    favicon: extractMediaUrl(metadataFields.favicon),
    seo: parseCmsSeo(metadataFields.seo ?? metadataFields.Seo) ?? legacySeo,
  }
}

/** Global site metadata (SEO fallbacks for service pages). */
export const fetchSiteMetadata = async (): Promise<MetadataContent> => {
  const raw = await cmsGet('metadata', CMS_API.metadata)
  return parseMetadataFields(raw)
}

function parseSameAsUrls(raw: unknown): string[] {
  if (!raw) return []
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === 'string') return item.trim()
        const fields = getStrapiFields(item)
        return pickStr(fields.url, fields.Url, fields.link, fields.Link)
      })
      .filter(Boolean)
  }
  return []
}

function parseOrganizationFields(organizationRaw: unknown): OrganizationContent | null {
  const organizationRoot =
    organizationRaw && typeof organizationRaw === 'object'
      ? (organizationRaw as { data?: unknown }).data
      : null
  const fields = getStrapiFields(organizationRoot)
  const name = pickStr(fields.name, fields.Name)
  if (!name) return null

  const latitudeRaw = fields.latitude ?? fields.Latitude
  const longitudeRaw = fields.longitude ?? fields.Longitude

  return {
    name,
    legalName: pickStr(fields.legalName, fields.LegalName),
    url: pickStr(fields.url, fields.Url),
    description: pickStr(fields.description, fields.Description),
    foundingDate: pickStr(fields.foundingDate, fields.FoundingDate),
    email: pickStr(fields.email, fields.Email),
    telephone: pickStr(fields.telephone, fields.Telephone),
    contactType: pickStr(fields.contactType, fields.ContactType),
    address: pickStr(fields.address, fields.Address),
    latitude: latitudeRaw == null || latitudeRaw === '' ? null : Number(latitudeRaw),
    longitude: longitudeRaw == null || longitudeRaw === '' ? null : Number(longitudeRaw),
    areaServed: pickStr(fields.areaServed, fields.AreaServed),
    priceRange: pickStr(fields.priceRange, fields.PriceRange),
    logo: extractMediaUrl(fields.logo ?? fields.Logo),
    sameAs: parseSameAsUrls(fields.sameAs ?? fields.SameAs),
  }
}

function parseFaqEntries(faqsRaw: unknown): FaqItem[] {
  if (!faqsRaw || typeof faqsRaw !== 'object') return []
  const data = (faqsRaw as { data?: unknown }).data
  const rows = Array.isArray(data) ? data : data ? [data] : []

  return sortByOrder(rows as Array<{ Order?: number | null }>)
    .map((entry) => {
      const raw = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : {}
      const fields = getStrapiFields(entry)
      return {
        id: Number(raw.id ?? fields.id ?? 0),
        question: pickStr(fields.Question, fields.question),
        answer: pickCmsMultilineText(fields.Answer, fields.answer),
        category: pickStr(fields.Category, fields.category),
        order: resolveCmsEntryOrder(entry),
      }
    })
    .filter((item) => item.question.trim() && item.answer.trim())
}

/** Organization schema source for JSON-LD. */
export const fetchOrganizationContent = async (): Promise<OrganizationContent | null> => {
  const raw = await cmsGet('organization', CMS_API.organization)
  return parseOrganizationFields(raw)
}

/** FAQ entries for FAQPage JSON-LD. */
export const fetchFaqContent = async (): Promise<FaqItem[]> => {
  const raw = await cmsGet('faqs', CMS_API.faqs)
  return parseFaqEntries(raw)
}

/** Contact block only — used on service pages instead of loading the full homepage CMS bundle. */
export const fetchContactContent = async (): Promise<ContactContent> => {
  const raw = await cmsGet('contact', CMS_API.contact)
  return parseContactFields(raw)
}

function parseServiceItems(serviceRaw: unknown): ServiceItem[] {
  const serviceRoot =
    serviceRaw && typeof serviceRaw === 'object'
      ? (serviceRaw as { data?: unknown }).data
      : null
  const serviceFields = getStrapiFields(serviceRoot)
  const serviceItemsRaw = Array.isArray(serviceFields.items) ? serviceFields.items : []
  return sortByOrder(serviceItemsRaw as Array<{ Order?: number | null }>).map((entry: unknown) => {
    const it = getStrapiFields(entry)
    const raw = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : {}
    const merged = { ...it, ...raw }
    return {
      id: Number(raw.id ?? it.id ?? 0),
      title: str(it.Title ?? it.title),
      description: str(it.Description ?? it.description),
      icon: extractMediaUrl(pickMediaField(merged)) || '',
      link: str(it.Link ?? it.link),
      slug: toSlug(str(it.slug ?? it.Slug ?? '')),
      order: Number(it.Order ?? it.order ?? 0),
    }
  })
}

/** Service cards from `GET /api/service` — same tickets as homepage Services slider. */
export const fetchServiceSectionItems = async (): Promise<ServiceItem[]> => {
  const raw = await cmsGet('service', CMS_API.service)
  return parseServiceItems(raw).filter((item) => item.title?.trim() || item.description?.trim())
}

export type ContactSubmissionResult =
  | { ok: true }
  | { ok: false; message: string }

/** Contact form — `POST /api/contact-submissions` (Strapi public create) */
export async function submitContactSubmission(
  payload: ContactSubmissionPayload
): Promise<ContactSubmissionResult> {
  try {
    await cmsClient.post(CMS_API.contactSubmissions, {
      data: {
        Full_Name: payload.fullName.trim(),
        Company_Name: payload.companyName.trim(),
        Company_Email: payload.companyEmail.trim(),
        Operational_Challenge: payload.operationalChallenge.trim(),
      },
    })
    cmsDebug('contact-submissions OK')
    return { ok: true }
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status
      const body = err.response?.data as
        | { error?: { message?: string } }
        | { message?: string }
        | undefined
      const apiMessage =
        (body && 'error' in body && body.error?.message) ||
        (body && 'message' in body && body.message) ||
        err.message
      cmsDebug('contact-submissions FAILED', status ?? 'network', apiMessage)
      if (status === 400) {
        return {
          ok: false,
          message: 'Please check your entries and try again.',
        }
      }
      if (status === 403) {
        return {
          ok: false,
          message: 'Contact form is not available. Please try again later.',
        }
      }
    } else {
      cmsDebug(
        'contact-submissions FAILED',
        err instanceof Error ? err.message : err
      )
    }
    return {
      ok: false,
      message: 'Failed to send message. Please try again later.',
    }
  }
}

/** Fails soft: one bad endpoint must not wipe the whole homepage. */
async function cmsGet(label: string, path: string): Promise<unknown> {
  const requestPath =
    isCmsDebugEnabled() ? `${path}${path.includes('?') ? '&' : '?'}_=${Date.now()}` : path

  const cached = cmsResponseCache.get(path)
  if (cached && Date.now() - cached.at < CMS_RESPONSE_CACHE_MS) {
    cmsDebug(`${label} cache hit`)
    return cached.value
  }

  const inflight = cmsInflight.get(path)
  if (inflight) {
    cmsDebug(`${label} awaiting in-flight request`)
    return inflight
  }

  const request = (async () => {
    try {
      const res = await cmsClient.get(requestPath)
      const body = res.data
      if (body && typeof body === 'object' && 'data' in body && (body as { data: unknown }).data === null) {
        cmsDebug(`${label} empty (data: null)`)
        return null
      }
      cmsDebug(`${label} OK`)
      cmsResponseCache.set(path, { at: Date.now(), value: body })
      return body
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status
        const url = err.config?.url ?? ''
        if (status === 403) {
          cmsDebug(
            `${label} FORBIDDEN (403) ${url} — enable Public find in Strapi or set VITE_CMS_API_TOKEN`
          )
        } else {
          cmsDebug(`${label} FAILED`, status ?? 'network', url, err.message)
        }
      } else {
        cmsDebug(`${label} FAILED`, err instanceof Error ? err.message : err)
      }
      return null
    } finally {
      cmsInflight.delete(path)
    }
  })()

  cmsInflight.set(path, request)
  return request
}

const parseFeature = (item: unknown): ServicePageFeature => {
  const raw = item && typeof item === 'object' ? (item as Record<string, unknown>) : {}
  const f = getStrapiFields(item)
  return {
    id: Number(raw.id ?? f.id ?? 0),
    title: str(f.Title ?? f.title),
    description: str(f.Description ?? f.description),
    icon: extractMediaUrl(f.Icon ?? f.icon),
  }
}

const parseTech = (item: unknown): ServicePageTech => {
  const raw = item && typeof item === 'object' ? (item as Record<string, unknown>) : {}
  const f = getStrapiFields(item)
  return {
    id: Number(raw.id ?? f.id ?? 0),
    title: str(f.Title ?? f.title),
    icon: extractMediaUrl(f.Icon ?? f.icon),
  }
}

export const fetchHomeCmsContent = async (): Promise<HomeCmsContent> => {
  try {
    return await fetchHomeCmsContentUnsafe()
  } catch (err) {
    cmsDebug('fetchHomeCmsContent fatal — returning empty defaults', err instanceof Error ? err.message : err)
    return { ...EMPTY_HOME_CMS }
  }
}

const fetchHomeCmsContentUnsafe = async (): Promise<HomeCmsContent> => {
  const [
    aboutRaw,
    businessRaw,
    contactRaw,
    primaryCtaResult,
    metadataRaw,
    partnerRaw,
    clearPathRaw,
    serviceRaw,
    organizationRaw,
    faqsRaw,
  ] = await Promise.all([
    cmsGet('about', CMS_API.about),
    cmsGet('business', CMS_API.business),
    cmsGet('contact', CMS_API.contact),
    fetchPrimaryCtaHeroRaw(),
    cmsGet('metadata', CMS_API.metadata),
    cmsGet('partner', CMS_API.partner),
    cmsGet('clear-path', CMS_API.clearPath),
    cmsGet('service', CMS_API.service),
    cmsGet('organization', CMS_API.organization),
    cmsGet('faqs', CMS_API.faqs),
  ])

  const aboutRoot =
    aboutRaw && typeof aboutRaw === 'object' ? (aboutRaw as { data?: unknown }).data : null
  const aboutFields = getStrapiFields(aboutRoot)

  const hero = parseHeroFromPrimaryCta(primaryCtaResult.raw)
  const heroCmsHttpStatus = primaryCtaResult.httpStatus

  const businessRoot =
    businessRaw && typeof businessRaw === 'object'
      ? (businessRaw as { data?: unknown }).data
      : null
  const businessFields = getStrapiFields(businessRoot)
  const businessItemsRaw = Array.isArray(businessFields.items) ? businessFields.items : []
  const businessItems = sortByOrder(businessItemsRaw as Array<{ Order?: number | null }>).map(
    (entry: unknown) => {
      const it = getStrapiFields(entry)
      const raw = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : {}
      const merged = { ...it, ...raw }
      return {
        id: Number(raw.id ?? it.id ?? 0),
        title: str(it.Title ?? it.title),
        description: str(it.Description ?? it.description),
        icon: extractMediaUrl(pickMediaField(merged)) || '',
        order: Number(it.Order ?? it.order ?? 0),
      }
    }
  )

  const serviceRoot =
    serviceRaw && typeof serviceRaw === 'object'
      ? (serviceRaw as { data?: unknown }).data
      : null
  const serviceFields = getStrapiFields(serviceRoot)
  const serviceItems = parseServiceItems(serviceRaw)

  const clearPathRoot =
    clearPathRaw && typeof clearPathRaw === 'object'
      ? (clearPathRaw as { data?: unknown }).data
      : null
  const clearPathFields = getStrapiFields(clearPathRoot)
  const stepsRaw = Array.isArray(clearPathFields.Steps) ? clearPathFields.Steps : []
  const clearPathSteps = sortByOrder(stepsRaw as Array<{ Order?: number | null }>).map(
    (entry: unknown) => {
      const st = getStrapiFields(entry)
      const raw = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : {}
      return {
        id: Number(raw.id ?? st.id ?? 0),
        title: str(st.Title),
        description: str(st.Description),
        order: Number(st.Order ?? 0),
      }
    }
  )

  const partnerRoot =
    partnerRaw && typeof partnerRaw === 'object'
      ? (partnerRaw as { data?: unknown }).data
      : null
  const partnerFields = getStrapiFields(partnerRoot)
  const partnerItemsRaw = pickStrapiComponentList(partnerFields, 'Items', 'items')
  const partners = partnerItemsRaw
    .map((entry, index) => {
      const it = getStrapiFields(entry)
      const raw = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : {}
      return {
        id: Number(raw.id ?? it.id ?? index + 1),
        name: str(it.Name ?? it.name),
        logo: extractMediaUrl(it.Logo ?? it.logo),
        order: resolveCmsEntryOrder(entry, index),
      }
    })
    .filter((p) => p.name.trim() || p.logo)
    .sort((a, b) => a.order - b.order || a.id - b.id)

  const parsed: HomeCmsContent = {
    hero,
    business: {
      title: str(businessFields.Title),
      description: str(businessFields.Description),
      items: businessItems,
    },
    services: {
      title: str(serviceFields.Title),
      description: str(serviceFields.Description),
      cta: pickStr(serviceFields.CTA, serviceFields.cta),
      items: serviceItems,
    },
    clearPath: {
      title: str(clearPathFields.Title),
      description: str(clearPathFields.Description),
      steps: clearPathSteps,
    },
    partners,
    about: (() => {
      const collage = parseAboutCollageFromCms(aboutFields.Image)
      return {
        title: str(aboutFields.Title),
        description: pickCmsMultilineText(aboutFields.Description, aboutFields.description),
        cta: pickStr(aboutFields.CTA, aboutFields.cta),
        images: collage.urls,
        image: collage.urls[0] ?? '',
        collageBySlot: collage.bySlot,
        footerStats: parseAboutFooterStats(aboutFields),
      }
    })(),
    contact: parseContactFields(contactRaw),
    metadata: parseMetadataFields(metadataRaw),
    organization: parseOrganizationFields(organizationRaw),
    faqs: parseFaqEntries(faqsRaw),
    heroCmsHttpStatus,
  }

  cmsDebug('parsed snapshot', {
    heroTitle: parsed.hero.title,
    heroCmsHttpStatus,
    businessItems: parsed.business.items.length,
    serviceItems: parsed.services.items.length,
    clearPathSteps: parsed.clearPath.steps.length,
    partners: parsed.partners.length,
    aboutTitle: parsed.about.title,
    contactEmail: parsed.contact.email,
  })

  return parsed
}


/** Same query as Postman “Get Service Page by Slug”. */
export const SERVICE_PAGE_POPULATE =
  'populate[our_workflow][populate][Icon]=true&populate[our_approach][populate][Icon]=true&populate[our_technology][populate][Icon]=true&populate[Services]=true&populate[seo][populate]=metaImage'

/** Documented CMS REST paths (see docs/DigiTrek-CMS-API.postman_collection.json) */
export const CMS_API = {
  about:
    '/api/about?populate[Image]=true&populate[About_footer_1]=true&populate[About_footer_2]=true&populate[About_footer_3]=true',
  business: '/api/business?populate[items][populate][Icon]=true',
  contact: '/api/contact',
  /** No populate — staging Strapi has no `backgroundImage` on this type (populate returns 400). */
  primaryCta: '/api/primary-cta',
  metadata: '/api/metadata?populate[favicon]=true&populate[seo][populate]=metaImage',
  partner: '/api/partner?populate[Items][populate][Logo]=true&publicationState=live',
  clearPath: '/api/clear-path?populate[Steps]=true',
  service: '/api/service?populate[items][populate][Icon]=true',
  organization: '/api/organization?populate[logo]=true&populate[sameAs]=true&publicationState=live',
  faqs: '/api/faqs?sort=Order:asc&publicationState=live&pagination[pageSize]=100',
  contactSubmissions: '/api/contact-submissions',
  servicePages: `/api/service-pages?${SERVICE_PAGE_POPULATE}`,
} as const

/** Axios body shape: `{ data: ServicePageEntry | ServicePageEntry[] }` — use `body.data[0]`, not `data.data[0]`. */
export function extractServicePageEntries(apiBody: unknown): unknown[] {
  if (!apiBody || typeof apiBody !== 'object') return []
  const data = (apiBody as { data?: unknown }).data
  if (Array.isArray(data)) return data
  if (data && typeof data === 'object') return [data]
  return []
}

function parseServicePageEntry(item: unknown): ServicePageContent {
  const rawEntry = item && typeof item === 'object' ? (item as Record<string, unknown>) : {}
  const f = getStrapiFields(item)

  // Exact Strapi API keys (see Postman “Get Service Page by Slug”)
  const servicesList = pickStrapiComponentList(f, 'Services')
  const workflowRaw = pickStrapiComponentList(f, 'our_workflow')
  const approachRaw = pickStrapiComponentList(f, 'our_approach')
  const technologyRaw = pickStrapiComponentList(f, 'our_technology')

  const title = str(f.Title ?? f.title)
  const slugField = str(f.Slug ?? f.slug)

  const page: ServicePageContent = {
    id: Number(rawEntry.id ?? f.id ?? 0),
    slug: slugField ? toSlug(slugField) : toSlug(title),
    title,
    header: str(f.Header ?? f.header),
    description: str(f.Description ?? f.description),
    seo: parseCmsSeo(f.seo ?? f.Seo),
    cta: pickStr(f.CTA, f.cta),
    approachTitle: pickCmsText(
      f.Our_Approach_Title,
      f.our_approach_title,
      f.OurApproachTitle
    ),
    approachParagraph: pickCmsMultilineText(
      f.Our_Approach_Paragraph,
      f.our_approach_paragraph,
      f.OurApproachParagraph
    ),
    technologyTitle: pickCmsText(
      f.Our_Technology_Title,
      f.our_technology_title,
      f.OurTechnologyTitle
    ),
    technologyParagraph: pickCmsMultilineText(
      f.Our_Technology_Paragraph,
      f.our_technology_paragraph,
      f.OurTechnologyParagraph
    ),
    problemCardsSectionTitle: pickCmsText(f.Services_Title, f.services_title, f.ServicesTitle),
    workflow: workflowRaw.map(parseFeature),
    approach: approachRaw.map(parseFeature),
    technology: technologyRaw.map(parseTech),
    problemCards: servicesList.map((s: unknown) => {
      const sf = getStrapiFields(s)
      const sr = s && typeof s === 'object' ? (s as Record<string, unknown>) : {}
      return {
        id: Number(sr.id ?? sf.id ?? 0),
        title: str(sf.Title ?? sf.title),
        description: str(sf.Description ?? sf.description),
      }
    }),
  }

  if (isCmsDebugEnabled()) {
    console.log('SERVICE PAGE RAW API FIELDS', {
      slug: f.slug ?? f.Slug,
      Services_Title: f.Services_Title,
      Our_Approach_Title: f.Our_Approach_Title,
      Our_Approach_Paragraph: f.Our_Approach_Paragraph,
      Our_Technology_Title: f.Our_Technology_Title,
      Our_Technology_Paragraph: f.Our_Technology_Paragraph,
      Services: servicesList.length,
      our_workflow: workflowRaw.length,
      our_approach: approachRaw.length,
      our_technology: technologyRaw.length,
    })
    console.log('SERVICE PAGE PARSED section headers', {
      slug: page.slug,
      problemCardsSectionTitle: page.problemCardsSectionTitle || '(empty)',
      approachTitle: page.approachTitle || '(empty)',
      approachParagraph: page.approachParagraph ? '(set)' : '(empty)',
      technologyTitle: page.technologyTitle || '(empty)',
      technologyParagraph: page.technologyParagraph ? '(set)' : '(empty)',
    })
    console.log('SERVICE PAGE PARSED', page)
  }

  return page
}

async function fetchServicePageRawBySlug(
  slug: string,
  populateQuery: string
): Promise<unknown> {
  const normalized = toSlug(slug)
  if (!normalized) return null

  const path = `/api/service-pages?filters[slug][$eq]=${encodeURIComponent(normalized)}&${populateQuery}`
  cmsDebug('GET', `${CMS_BASE_URL}${path}`)
  return cmsGet(`service-pages[slug=${normalized}]`, path)
}

/** Recommended: fetch one service page by CMS `slug` (matches `/services/:slug`). */
export const fetchServicePageBySlug = async (slug: string): Promise<ServicePageContent | null> => {
  const normalized = toSlug(slug)
  if (!normalized) return null

  const raw = await fetchServicePageRawBySlug(normalized, SERVICE_PAGE_POPULATE)
  const entries = extractServicePageEntries(raw)
  if (!entries.length) return null

  const page = parseServicePageEntry(entries[0])
  return page
}

export const fetchServicePages = async (): Promise<ServicePageContent[]> => {
  if (
    cachedServicePagesList &&
    Date.now() - cachedServicePagesListAt < CMS_RESPONSE_CACHE_MS
  ) {
    cmsDebug('service-pages list cache hit')
    return cachedServicePagesList
  }

  const raw = await cmsGet('service-pages', `/api/service-pages?${SERVICE_PAGE_POPULATE}`)
  const pages = extractServicePageEntries(raw).map(parseServicePageEntry)
  cachedServicePagesList = pages
  cachedServicePagesListAt = Date.now()
  return pages
}
